import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, ShieldCheck } from "lucide-react";

const ROLES = [
  { v: "head_coach", l: "Head Coach" },
  { v: "offensive_coordinator", l: "Offensive Coordinator" },
  { v: "defensive_coordinator", l: "Defensive Coordinator" },
  { v: "special_teams", l: "Special Teams Coordinator" },
  { v: "position_coach", l: "Position Coach" },
  { v: "strength_coach", l: "Strength Coach" },
  { v: "assistant_coach", l: "Assistant Coach" },
  { v: "athletic_director", l: "Athletic Director" },
];

export default function Register() {
  const { user, register, error } = useAuth();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "assistant_coach", title: ""
  });
  const [invitePreview, setInvitePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/billing/invite/${inviteToken}`);
        setInvitePreview(data);
        setForm(f => ({ ...f, email: data.email, role: data.role }));
      } catch { setInvitePreview({ error: "Invalid or expired invite" }); }
    })();
  }, [inviteToken]);

  if (user && user !== false && user !== null) return <Navigate to="/" replace />;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register({ ...form, invite_token: inviteToken || undefined });
    setBusy(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-background">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/login" className="text-[10px] font-condensed tracking-[0.4em] uppercase text-amber hover:underline">← Back</Link>
          <div className="font-display text-5xl tracking-wider text-white uppercase leading-none mt-3">
            {invitePreview && !invitePreview.error ? "Join The Program" : "Join The Staff"}
          </div>
          <p className="text-slate-400 text-sm mt-3">
            {invitePreview && !invitePreview.error
              ? `Accept your invite to ${invitePreview.program_name}.`
              : "Create your Gridiron OS coach account."}
          </p>
        </div>

        {invitePreview?.error && (
          <div className="border-l-2 border-red-500 pl-3 py-2 bg-red-500/5 text-sm text-red-400 mb-4" data-testid="invite-error">
            {invitePreview.error}
          </div>
        )}

        {invitePreview && !invitePreview.error && (
          <div className="bg-surface border border-amber/40 p-4 mb-6" data-testid="invite-preview">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber" />
              <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-amber">Invited to {invitePreview.program_name}</div>
            </div>
            <div className="text-sm text-slate-200 mt-1 flex items-center gap-2">
              <Mail className="w-3 h-3 text-slate-500" /> {invitePreview.email}
              <span className="text-slate-500">·</span>
              <span className="text-slate-300">{invitePreview.role.replace(/_/g, " ")}</span>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Full Name</Label>
            <Input data-testid="reg-name" value={form.name} onChange={(e)=>set("name", e.target.value)} required
              className="mt-2 bg-surface border-surface-3 focus:border-amber h-11 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Email</Label>
            <Input data-testid="reg-email" type="email" value={form.email}
              onChange={(e)=>set("email", e.target.value)} required
              readOnly={!!(invitePreview && !invitePreview.error)}
              className="mt-2 bg-surface border-surface-3 focus:border-amber h-11 text-sm read-only:opacity-70" />
          </div>
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Password</Label>
            <Input data-testid="reg-password" type="password" value={form.password} onChange={(e)=>set("password", e.target.value)} required minLength={6}
              className="mt-2 bg-surface border-surface-3 focus:border-amber h-11 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Role</Label>
            <Select value={form.role} onValueChange={(v)=>set("role", v)} disabled={!!(invitePreview && !invitePreview.error)}>
              <SelectTrigger data-testid="reg-role" className="mt-2 bg-surface border-surface-3 h-11 rounded-none text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-surface-3 rounded-none">
                {ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Title / Position (optional)</Label>
            <Input data-testid="reg-title" value={form.title} onChange={(e)=>set("title", e.target.value)} placeholder="e.g., WR Coach"
              className="mt-2 bg-surface border-surface-3 focus:border-amber h-11 text-sm" />
          </div>

          {error && (
            <div className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-2 bg-red-500/5" data-testid="register-error">
              {error}
            </div>
          )}

          <Button
            type="submit" disabled={busy} data-testid="register-submit"
            className="w-full h-12 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-sm rounded-none"
          >
            {busy ? "Creating…" : (inviteToken ? "Accept Invite & Join" : "Create Account")}
          </Button>
        </form>
      </div>
    </div>
  );
}
